import React, { useState, useEffect } from "react";
import html2pdf from "html2pdf.js";
import Swal from "sweetalert2";
import axios from "axios";

const roles = ["Workstation", "Assistant Professor", "Senior Registrar"];

const initialData = roles.reduce((acc, role) => {
  acc[role] = [""];
  return acc;
}, {});

const generatePDFBlob = async ({ language, month, departments }) => {
  const isUrdu = language === "ur";

  const urduRoles = {
    "Workstation": "ورک سٹیشن",
    "Assistant Professor": "اسسٹنٹ پروفیسر",
    "Senior Registrar": "سینئر رجسٹرار"
  };

  const hospitalName = isUrdu
    ? "راولپنڈی انسٹیٹیوٹ آف کارڈیالوجی"
    : "Rawalpindi Institute of Cardiology";
  const subAddress = isUrdu
    ? "راول روڈ، راولپنڈی"
    : "Rawal Road, Rawalpindi";
  const title = isUrdu
    ? "کنسلٹنٹ وارڈ روسٹر"
    : "Consultant Ward Roster";

  const leftLogoUrl = "../../assets/img/logo/new-logo.png";
  const rightLogoUrl = "../../assets/img/logo/punjab-logo.png";

  const roles = ["Workstation", "Assistant Professor", "Senior Registrar"];

  const autoTranslateToUrdu = async (text) => {
    if (!isUrdu || !text?.trim()) return text;
    try {
      const res = await fetch(
        `https://translate.googleapis.com/translate_a/single?client=gtx&sl=en&tl=ur&dt=t&q=${encodeURIComponent(
          text
        )}`
      );
      const json = await res.json();
      return json?.[0]?.[0]?.[0] || text;
    } catch (err) {
      console.error("Translation error:", err);
      return text;
    }
  };


  
  const translatedRows = await Promise.all(
    Array.from({ length: Math.max(...roles.map((r) => departments[r]?.length || 0)) }).map(
      async (_, i) => {
        const cells = await Promise.all(
          roles.map(async (role) => {
            const value = departments[role]?.[i] || "";
            const translated = await autoTranslateToUrdu(value);
            return `<td>${translated}</td>`;
          })
        );
        return `<tr>${cells.join("")}</tr>`;
      }
    )
  );

  const roleHeaders = roles
    .map((role) => `<th>${isUrdu ? urduRoles[role] || role : role}</th>`)
    .join("");

  const content = `
    <div style="font-family: ${isUrdu ? "'Noto Nastaliq Urdu'" : "'Segoe UI', sans-serif"};
                direction: ${isUrdu ? "rtl" : "ltr"};
                padding: 20px;
                font-size: 13px;
                color: #2c3e50;">
      <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px;">
        <div style="width: 20%;"><img src="${leftLogoUrl}" style="height: 60px;" /></div>
        <div style="text-align: center; width: 60%;">
          <h2 style="margin: 0; font-weight: 700;">${hospitalName}</h2>
          <p style="margin: 2px 0; font-size: 13px; color: #555;">${subAddress}</p>
          <h4 style="margin: 6px 0; color: #444;">${title} – ${month}</h4>
        </div>
        <div style="width: 20%; text-align: ${isUrdu ? "left" : "right"};">
          <img src="${rightLogoUrl}" style="height: 60px;" />
        </div>
      </div>

      <table style="width: 100%; border-collapse: collapse; font-size: 12px; text-align: center;">
        <thead>
          <tr style="background: linear-gradient(to right, #0052D4, #4364F7); color: #fff;">
            ${roleHeaders}
          </tr>
        </thead>
        <tbody>
          ${translatedRows.join("")}
        </tbody>
      </table>

      <div style="margin-top: 80px; display: flex; justify-content: center; gap: 80px; font-size: 13px;">
        <div style="text-align: center;">
          <div style="border-top: 1px solid #333; width: 220px; margin: auto; margin-bottom: 6px;"></div>
          <strong>Dr. Ephraim Sheraz</strong><br />
          <span>${isUrdu ? "اسسٹنٹ پروفیسر" : "Assistant Professor"}</span><br />
          <span>${hospitalName}</span>
        </div>
        <div style="text-align: center;">
          <div style="border-top: 1px solid #333; width: 220px; margin: auto; margin-bottom: 6px;"></div>
          <strong>Dr. Asim Javed</strong><br />
          <span>${isUrdu ? "ایچ او ڈی کارڈیالوجی" : "HOD Cardiology"}</span><br />
          <span>${hospitalName}</span>
        </div>
      </div>

      <p style="text-align:center; margin-top: 40px; font-size: 10px; color: #888;">
        © ${new Date().getFullYear()} ${hospitalName}. All rights reserved.
      </p>
    </div>
  `;

  const blob = await html2pdf()
    .from(content)
    .set({
      margin: 0.3,
      filename: `Consultant_Ward_Roster_${month}_${language.toUpperCase()}.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: "in", format: "a4", orientation: "portrait" }
    })
    .output("blob");

  return blob;
};


const ConsultantWardRoster = () => {
  const [departments, setDepartments] = useState(initialData);
  const [month, setMonth] = useState(new Date().toLocaleString("default", { month: "long", year: "numeric" }));
  const [selectedMonth, setSelectedMonth] = useState("");

  useEffect(() => {
    if (selectedMonth) loadRoster(selectedMonth);
  }, [selectedMonth]);

  const addRow = () => {
    setDepartments((prev) => {
      const updated = { ...prev };
      roles.forEach(role => {
        updated[role] = [...updated[role], ""];
      });
      return updated;
    });
  };

  const updateValue = (role, idx, value) => {
    setDepartments((prev) => {
      const updated = [...prev[role]];
      updated[idx] = value;
      return { ...prev, [role]: updated };
    });
  };

  const saveRoster = async () => {
    try {
      const res = await axios.post("https://ricgop.online/backend/consultant_roster.php", { month, departments });
      Swal.fire("✅ Saved", "Roster saved successfully.", "success");
    } catch {
      Swal.fire("❌ Error", "Failed to save roster.", "error");
    }
  };

const loadRoster = async (m) => {
  try {
    const res = await axios.get("https://ricgop.online/backend/consultant_roster.php", { params: { month: m } });
    if (res.data.success && res.data.departments) {
      // Ensure all roles are initialized
      const loadedData = { ...initialData };
      for (const role of roles) {
        loadedData[role] = res.data.departments[role] ?? [""];
      }

      setDepartments(loadedData);
      setMonth(m);
      Swal.fire("✅ Loaded", "Roster loaded.", "success");
    } else {
      Swal.fire("⚠️ Not Found", "No roster found for this month.", "warning");
      setDepartments(initialData); // Reset to empty form
    }
  } catch {
    Swal.fire("❌ Error", "Failed to load roster.", "error");
  }
};


  const downloadPDFs = async () => {
    Swal.fire({ title: 'Generating...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
    try {
      const [enBlob, urBlob] = await Promise.all([
        generatePDFBlob({ language: "en", month, departments }),
        generatePDFBlob({ language: "ur", month, departments }),
      ]);

      const link = document.createElement("a");
      link.href = URL.createObjectURL(enBlob);
      link.download = `Consultant_Roster_${month}_EN.pdf`;
      link.click();

      const link2 = document.createElement("a");
      link2.href = URL.createObjectURL(urBlob);
      link2.download = `Consultant_Roster_${month}_UR.pdf`;
      link2.click();

      Swal.fire("✅ Success", "PDFs downloaded.", "success");
    } catch {
      Swal.fire("❌ Error", "PDF generation failed.", "error");
    }
  };

  const emailRoster = async () => {
    try {
      Swal.fire({ title: "Preparing PDFs...", html: "Please wait <b id='progress'>0</b>%", showConfirmButton: false, allowOutsideClick: false });

      const enBlob = await generatePDFBlob({ language: "en", month, departments });
      const urBlob = await generatePDFBlob({ language: "ur", month, departments });

      const formData = new FormData();
      formData.append("englishPDF", new File([enBlob], `Consultant_Roster_${month}_EN.pdf`, { type: "application/pdf" }));
      formData.append("urduPDF", new File([urBlob], `Consultant_Roster_${month}_UR.pdf`, { type: "application/pdf" }));
      formData.append("month", month);

      await axios.post("https://ricgop.online/backend/email_consultant_roster.php", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          const percent = Math.round((e.loaded * 100) / e.total);
          const el = document.getElementById("progress");
          if (el) el.textContent = percent.toString();
        },
      });

      Swal.fire("📧 Emailed", "Roster sent successfully!", "success");
    } catch {
      Swal.fire("❌ Error", "Failed to send email.", "error");
    }
  };

  return (
    <div className="container bg-light p-4 rounded-4 shadow border">
      <h4 className="text-center mb-3">📋 Consultant Ward Roster</h4>

      <div className="row g-3 mb-3">
        <div className="col-md-6">
          <label className="form-label fw-semibold">📅 Roster Month</label>
          <select className="form-select" value={month} onChange={(e) => setMonth(e.target.value)}>
            {Array.from({ length: 12 }, (_, i) => {
              const d = new Date();
              d.setMonth(d.getMonth() + i);
              const label = d.toLocaleString("default", { month: "long", year: "numeric" });
              return <option key={i} value={label}>{label}</option>;
            })}
          </select>
        </div>
        <div className="col-md-6">
  <label className="form-label fw-semibold">🔁 Load Existing</label>
  <select
    className="form-select"
    value={selectedMonth}
    onChange={(e) => setSelectedMonth(e.target.value)}
  >
    <option value="">-- Select Month --</option>
   {Array.from({ length: 19 }, (_, i) => {
  const d = new Date();
  d.setMonth(d.getMonth() - 12 + i);
  const label = d.toLocaleString("default", { month: "long", year: "numeric" });
  const key = d.toISOString(); // ensures uniqueness like 2025-12-01T00:00:00.000Z
  return (
    <option key={key} value={label}>
      {label}
    </option>
  );
})}
  </select>
</div>

      </div>

      <div className="table-responsive">
        <table className="table table-bordered">
          <thead className="table-light">
            <tr>{roles.map((role) => <th key={role}>{role}</th>)}</tr>
          </thead>
                    <tbody>
            {Array.from({
                length: Math.max(...roles.map((r) => (departments[r]?.length || 0))),
            }).map((_, rowIndex) => (
                <tr key={rowIndex}>
                {roles.map((role, i) => (
                    <td key={i}>
                    <input
                        className="form-control"
                        value={departments[role]?.[rowIndex] || ""}
                        onChange={(e) => updateValue(role, rowIndex, e.target.value)}
                        placeholder="Write here"
                    />
                    </td>
                ))}
                </tr>
            ))}
            </tbody>
        </table>
      </div>

      <div className="my-3">
        <button className="btn btn-outline-secondary me-2" onClick={addRow}>➕ Add Row</button>
        <button className="btn btn-success me-2" onClick={saveRoster}>💾 Save</button>
        <button className="btn btn-primary me-2" onClick={downloadPDFs}>📥 Download</button>
        <button className="btn btn-outline-success" onClick={emailRoster}>📧 Email</button>
      </div>
    </div>
  );
};

export default ConsultantWardRoster;
